import React, { useEffect, useState } from 'react';
import { obtenerConfiguracion } from '../../services/configTiendaApi';
import { getCategorias } from '../../services/categoriasServicio';
import { getProductosCatalogo } from '../../services/productosApi';
import config from '../../config/apiConfig';

const CheckIcon = ({ hecho }) => (
    <div style={{
        width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hecho ? '#22c55e' : '#e5e7eb',
        color: hecho ? '#fff' : '#9ca3af',
        fontWeight: 800, fontSize: '0.85rem'
    }}>
        {hecho ? '✓' : ''}
    </div>
);

// 🚩 Wizard de primeros pasos, unificado para plan POS y RESTAURANTE.
// Se muestra como modal flotante sobre el Home (tarjetas de indicadores),
// no como pantalla completa — el usuario lo cierra con la X cuando quiere.
function WizardConfiguracion({ plan, onClose }) {
    const esRestaurante = plan === 'RESTAURANTE';
    const [loading, setLoading] = useState(true);
    const [tienda, setTienda] = useState({ nombre: '' });
    const [numCategorias, setNumCategorias] = useState(0);
    const [numProductos, setNumProductos] = useState(0);
    const [numStaff, setNumStaff] = useState(0);
    const [numMesas, setNumMesas] = useState(0);

    useEffect(() => {
        const cargar = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = { token };
                const llamadas = [
                    obtenerConfiguracion().catch(() => null),
                    getCategorias().catch(() => []),
                    getProductosCatalogo().catch(() => ({ productos: [] })),
                    fetch(`${config.api_url}/api/staff`, { headers }).then(r => r.json()).catch(() => ({ data: [] })),
                ];
                if (esRestaurante) {
                    llamadas.push(fetch(`${config.api_url}/api/v1/mesas`, { headers }).then(r => r.json()).catch(() => []));
                }
                const [cfg, categorias, catalogo, staffRes, mesasRes] = await Promise.all(llamadas);
                if (cfg) setTienda({ nombre: cfg.nombre || '' });
                setNumCategorias((categorias || []).length);
                setNumProductos(((catalogo && catalogo.productos) || []).length);
                setNumStaff(((staffRes && staffRes.data) || []).length);
                if (esRestaurante) setNumMesas((Array.isArray(mesasRes) ? mesasRes : []).length);
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, [esRestaurante]);

    const pasos = [
        { id: 'categorias', label: 'Crea tus categorías de productos', hecho: numCategorias > 0, href: '/categorias/' },
        { id: 'productos', label: 'Agrega tu carta con precios', hecho: numProductos > 0, href: '/productos/home/' },
        ...(esRestaurante ? [{ id: 'mesas', label: 'Configura las mesas de tu local', hecho: numMesas > 0, href: '/admin/mesas/', desc: 'Nombre, sector y capacidad de cada mesa' }] : []),
        { id: 'staff', label: 'Crea el PIN de acceso de tu equipo', hecho: numStaff > 0, href: '/staff/', desc: 'Cada cajero, garzón o cocina necesita su propio PIN' },
    ];

    const completados = pasos.filter(p => p.hecho).length;
    const listo = completados === pasos.length;

    // Una vez que queda todo completo, no hace falta seguir mostrando este
    // modal solo cada vez que se entra a Inicio -- se marca y Home.js deja
    // de abrirlo por defecto (igual se puede seguir cerrando/abriendo a mano
    // dentro de esta misma visita).
    useEffect(() => {
        if (!loading && listo) {
            localStorage.setItem('komity_wizard_completado', 'true');
            onClose();
        }
    }, [loading, listo, onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15, 12, 41, 0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 1050, padding: '16px'
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="shadow-lg"
                style={{ background: '#fff', borderRadius: '16px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}
            >
                <button
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        position: 'absolute', top: '14px', right: '14px', width: '32px', height: '32px',
                        borderRadius: '50%', border: 'none', background: '#f3f4f6', color: '#4b5563',
                        fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', zIndex: 1
                    }}
                >
                    ✕
                </button>

                <div className="p-4">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                    ) : (
                        <>
                            <div className="text-center mb-4">
                                <h1 className="h4 font-weight-bold text-dark mb-1">
                                    {listo ? '¡Todo listo! 🎉' : `Hola${tienda.nombre ? ', ' + tienda.nombre : ''} 👋`}
                                </h1>
                                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                    {listo
                                        ? 'Tu negocio está configurado y operativo.'
                                        : `Sigue estos pasos para poner en marcha tu ${esRestaurante ? 'restaurante' : 'punto de venta'}.`}
                                </p>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>Progreso de configuración</span>
                                <span className="badge badge-pill" style={{
                                    background: listo ? '#dcfce7' : '#fff7ed',
                                    color: listo ? '#16a34a' : '#c2410c',
                                    fontWeight: 700, padding: '6px 12px'
                                }}>{completados}/{pasos.length}</span>
                            </div>
                            <div className="progress mb-3" style={{ height: '8px', borderRadius: '8px' }}>
                                <div className="progress-bar" style={{ width: `${(completados / pasos.length) * 100}%`, borderRadius: '8px', background: listo ? '#22c55e' : '#f97316' }}></div>
                            </div>

                            {pasos.map((paso) => (
                                <a key={paso.id} href={paso.href} className="text-decoration-none">
                                    <div className="d-flex align-items-start py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <CheckIcon hecho={paso.hecho} />
                                        <div className="ml-3" style={{ flex: 1 }}>
                                            <div style={{ color: paso.hecho ? '#9ca3af' : '#1a1a2e', textDecoration: paso.hecho ? 'line-through' : 'none', fontWeight: 500, fontSize: '0.9rem' }}>
                                                {paso.label}
                                            </div>
                                            {paso.desc && !paso.hecho && (
                                                <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' }}>{paso.desc}</div>
                                            )}
                                        </div>
                                        {!paso.hecho && (
                                            <span className="ml-auto text-primary" style={{ fontSize: '0.8rem', fontWeight: 600, flexShrink: 0 }}>Ir →</span>
                                        )}
                                    </div>
                                </a>
                            ))}

                            <p className="mb-0 small text-muted text-center mt-4">
                                ¿Necesitas ayuda? Escríbenos por <a href="https://wa.me/56920039433" target="_blank" rel="noreferrer" className="font-weight-bold">WhatsApp</a>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default WizardConfiguracion;
